import { cva, type VariantProps } from 'class-variance-authority';
import { format } from 'date-fns';
import {
  AlertCircle,
  Ban,
  Bot,
  CalendarClock,
  Check,
  CheckCheck,
  CircleSlash,
  Clock,
  ClockAlert,
  Download,
  NotebookPen,
  Paperclip,
  Settings,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Badge } from './badge';
import { SenderTooltip } from './sender-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface MessageAttachment {
  name: string;
  size?: string;
  url?: string;
  type?: string;
}

const messageBubbleVariants = cva('flex gap-2 group', {
  variants: {
    variant: {
      sent: 'flex-row-reverse',
      received: '',
    },
  },
  defaultVariants: {
    variant: 'received',
  },
});

const messageContentVariants = cva('p-3 break-words text-sm leading-relaxed', {
  variants: {
    variant: {
      sent: 'ml-auto',
      received: '',
    },
    sender: {
      human: 'max-w-[85%] rounded-lg',
      ai: 'border shadow-lg/3 rounded-md',
      system: 'bg-card border border-border-heavy/75 text-foreground rounded-md',
    },
  },
  compoundVariants: [
    {
      variant: 'sent',
      sender: 'human',
      className: 'bg-muted',
    },
    {
      variant: 'sent',
      sender: 'ai',
      className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-700',
    },
    {
      variant: 'received',
      sender: 'human',
      className: 'bg-accent text-foreground',
    },
  ],
  defaultVariants: {
    variant: 'received',
    sender: 'human',
  },
});

const avatarVariants = cva('text-xs font-medium', {
  variants: {
    variant: {
      sent: 'bg-muted text-foreground',
      received: 'bg-accent text-accent-foreground',
    },
    sender: {
      human: '',
      ai: 'bg-gradient-to-br from-amber-50 to-amber-300 text-amber-600',
      system: 'bg-muted-foreground/20 border text-muted-foreground',
    },
  },
  compoundVariants: [
    {
      variant: 'sent',
      sender: ['ai', 'system'],
      className: 'bg-card',
    },
  ],
  defaultVariants: {
    variant: 'received',
    sender: 'human',
  },
});

export interface MessageBubbleProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'>,
    VariantProps<typeof messageBubbleVariants> {
  /**
   * The content of the message
   */
  content: string | React.ReactNode;

  /**
   * Type of sender
   * @default 'human'
   */
  sender?: 'human' | 'ai' | 'system';

  /**
   * The sender's name/title
   */
  senderName?: string;

  /**
   * Shown in a tooltip over the sender's name (e.g. a full name behind a first-name label)
   */
  senderTooltip?: string;

  /**
   * The sender's avatar URL
   */
  avatarUrl?: string;

  /**
   * Custom avatar fallback (e.g., initials/emoji)
   */
  avatarFallback?: string;

  /**
   * Custom icon component to display instead of avatar
   */
  icon?: React.ComponentType<{ className?: string }>;

  /**
   * Tooltip text for the icon (e.g., 'SMS', 'WhatsApp', 'Email')
   */
  iconDescription?: string;

  /**
   * Timestamp for the message
   */
  timestamp?: Date | string;

  /**
   * Custom timestamp format string
   * @default 'h:mm a'
   */
  timestampFormat?: string;

  /**
   * List of attachments
   */
  attachments?: MessageAttachment[];

  /**
   * Optional badge text to show next to sender name
   */
  badgeText?: string;

  /**
   * Badge variant
   */
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
  /** Rendered after the timestamp, for a caller that has something to say about this message. */
  metaSuffix?: React.ReactNode;

  /**
   * Show avatar
   * @default true
   */
  showAvatar?: boolean;

  /**
   * Show sender name
   * @default true
   */
  showSenderName?: boolean;

  /**
   * Show timestamp
   * @default true
   */
  showTimestamp?: boolean;

  /**
   * Custom className for the content area
   */
  contentClassName?: string;

  /**
   * Callback when attachment is clicked
   */
  onAttachmentClick?: (attachment: MessageAttachment) => void;

  /**
   * Delivery status for outbound messages
   * Shows checkmarks/icons based on status
   */
  deliveryStatus?: 'scheduled' | 'pending' | 'queued' | 'sent' | 'delivered' | 'failed' | 'rejected' | 'cancelled' | 'expired' | 'skipped';

  /**
   * Why the message never reached the recipient — surfaced in the status tooltip
   * for every non-delivery state (failed, rejected, cancelled, expired), so the
   * icon is never the only explanation.
   */
  failureReason?: string | null;

  /**
   * Colour classes for the status indicator, replacing the neutral default. Lets a
   * surface give the lifecycle its own emphasis — amber for not-sent-yet, say —
   * without changing how the indicator reads everywhere else.
   */
  deliveryStatusClassName?: string;
}

export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  (
    {
      variant = 'received',
      sender = 'human',
      content,
      senderName,
      senderTooltip,
      avatarUrl,
      avatarFallback,
      icon: Icon,
      iconDescription,
      timestamp,
      timestampFormat = 'h:mm a',
      attachments,
      badgeText,
      badgeVariant = 'default',
      metaSuffix,
      showAvatar = true,
      showSenderName = true,
      showTimestamp = true,
      className,
      contentClassName,
      onAttachmentClick,
      deliveryStatus,
      failureReason,
      deliveryStatusClassName,
      ...props
    },
    ref
  ) => {
    // Render delivery status icon for outbound messages
    const renderDeliveryStatus = () => {
      if (!deliveryStatus || variant !== 'sent') return null;

      const statusConfig = {
        scheduled: { icon: CalendarClock, className: 'text-muted-foreground', tooltip: 'Scheduled' },
        pending: { icon: Clock, className: 'text-muted-foreground', tooltip: 'Sending...' },
        queued: { icon: Clock, className: 'text-muted-foreground', tooltip: 'Queued' },
        sent: { icon: Check, className: 'text-muted-foreground', tooltip: 'Sent' },
        delivered: { icon: CheckCheck, className: 'text-primary', tooltip: 'Delivered' },
        failed: { icon: AlertCircle, className: 'text-destructive', tooltip: failureReason || 'Failed to send' },
        rejected: { icon: AlertCircle, className: 'text-destructive', tooltip: failureReason || 'Rejected' },
        cancelled: { icon: Ban, className: 'text-muted-foreground', tooltip: failureReason || 'Cancelled' },
        expired: { icon: ClockAlert, className: 'text-muted-foreground', tooltip: failureReason || 'Expired' },
        skipped: { icon: CircleSlash, className: 'text-muted-foreground', tooltip: failureReason || 'Not sent' },
      };

      const config = statusConfig[deliveryStatus];
      if (!config) return null;
      const StatusIcon = config.icon;

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn('inline-flex ml-1', deliveryStatusClassName ?? config.className)}>
              <StatusIcon className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{config.tooltip}</TooltipContent>
        </Tooltip>
      );
    };

    // Generate avatar fallback from sender name if not provided
    const generatedFallback =
      avatarFallback ||
      (senderName
        ? senderName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
        : sender === 'ai'
          ? 'AI'
          : sender === 'system'
            ? 'S'
            : 'U');

    // Format timestamp
    const formattedTimestamp = timestamp
      ? typeof timestamp === 'string'
        ? format(new Date(timestamp), timestampFormat)
        : format(timestamp, timestampFormat)
      : null;

    return (
      <div ref={ref} className={cn(messageBubbleVariants({ variant }), className)} {...props}>
        {/* Avatar or Icon */}

        {showAvatar && Icon ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="size-7 shrink-0 rounded-full bg-muted flex items-center justify-center">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            {iconDescription && <TooltipContent>{iconDescription}</TooltipContent>}
          </Tooltip>
        ) : showAvatar ? (
          <Avatar className="size-8 shrink-0 ">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={senderName} />}
            <AvatarFallback className={cn(avatarVariants({ variant, sender }))}>
              {sender === 'ai' && !avatarFallback ? <Bot className="h-4 w-4" /> : generatedFallback}
            </AvatarFallback>
          </Avatar>
        ) : null}

        {/* Message Content */}
        <div className={cn('flex-1 space-y-1 min-w-0', variant === 'sent' && 'items-end')}>
          {/* Header: Sender Name, Badge, Timestamp */}
          {(showSenderName || showTimestamp || badgeText) && (
            <div className={cn('flex items-center gap-2 flex-wrap', variant === 'sent' && 'justify-end')}>
              {showSenderName && senderName && (
                <SenderTooltip fullName={senderTooltip}>
                  <span className="font-semibold text-sm">{senderName}</span>
                </SenderTooltip>
              )}

              {badgeText && badgeText !== 'System' && badgeText !== 'Internal Note' && <Badge variant={badgeVariant}>{badgeText}</Badge>}

              {badgeText && badgeText === 'Internal Note' && (
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="warning" className="text-xs gap-1">
                      <NotebookPen className="h-3 w-3" />
                      {badgeText}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This message cannot be seen by customers.</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {sender === 'ai' && !badgeText && (
                <Badge variant="warning" className="text-xs gap-1">
                  <Bot className="h-3 w-3" />
                  AI Assistant
                </Badge>
              )}

              {sender === 'system' && !badgeText && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Settings className="h-3 w-3" />
                  System
                </Badge>
              )}

              {showTimestamp && formattedTimestamp && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1 font-mono">
                  {formattedTimestamp}
                  {metaSuffix}
                  {renderDeliveryStatus()}
                </span>
              )}
            </div>
          )}

          {/* Message Bubble */}
          <div className={cn(messageContentVariants({ variant, sender }), contentClassName)}>
            {/* Content */}
            <div>{typeof content === 'string' ? <pre className="whitespace-pre-wrap font-sans">{content}</pre> : content}</div>

            {/* Attachments */}
            {attachments && attachments.length > 0 && (
              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-border/50">
                {attachments.map((attachment) => (
                  <button
                    key={attachment.name}
                    type="button"
                    onClick={() => onAttachmentClick?.(attachment)}
                    className={cn(
                      'flex items-center justify-between gap-2 px-3 py-2 rounded-md text-xs transition-colors cursor-pointer',
                      'hover:bg-background/50',
                      variant === 'sent' ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'bg-background/50'
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span className="font-medium truncate">{attachment.name}</span>
                      {attachment.size && <span className="opacity-70 shrink-0">({attachment.size})</span>}
                    </div>
                    <Download className="h-3.5 w-3.5 shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

MessageBubble.displayName = 'MessageBubble';

export { avatarVariants, messageBubbleVariants, messageContentVariants };
